import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Separator } from '../../../components/ui/separator';
import { Switch } from '../../../components/ui/switch';
import { useApiError } from '../../../shared/hooks/useApiError';
import { User, UserStatus, UserTitle, JobFunction, JobLevel } from '@/shared/users/types';
import { JOB_FUNCTION_OPTIONS, JOB_LEVEL_OPTIONS } from '@/shared/users/constants/jobProfile';
import { useCreateUser, useUpdateUser } from '@/shared/users/hooks/useUsers';
import { useCompanies } from '@/shared/users/hooks/useCompanies';
import { useGroups } from '@/shared/users/hooks/useGroups';
import { useAuth } from '../../../shared/auth';
import { DialogForm, PersonNameFields } from '../../../shared/components/common';
import { ModuleAccessSection } from '../../../shared/components/admin/ModuleAccessSection';
import { TOGGLEABLE_MODULE_IDS } from '../../../shared/config/modules.config';

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  user?: User | null;
}


interface FormData {
  email: string;
  title: string | null;
  first_name: string;
  middle_name: string;
  last_name: string;
  suffix: string;
  password: string;
  primary_company_id: string;
  is_admin: boolean;
  status: UserStatus;
  group_ids: string[];
  enabled_modules: string[] | null;
  job_function: JobFunction | null;
  job_function_custom: string;
  job_level: JobLevel | null;
}

export function UserForm({ open, onClose, user }: UserFormProps) {
  const { t } = useTranslation(['user_management', 'common', 'admin']);
  const { user: currentUser } = useAuth();
  const { handleMutationError } = useApiError({ showToast: false });
  const isEdit = !!user;
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();

  const { data: companiesData } = useCompanies({ limit: 1000 });
  const { data: groupsData } = useGroups({ limit: 1000 });

  const companies = companiesData?.data || [];
  const groups = groupsData?.data || [];

  // Determine available modules for this user based on tenant settings
  const tenantEnabledModules = currentUser?.tenant_enabled_modules;
  const availableModules = tenantEnabledModules ?? [...TOGGLEABLE_MODULE_IDS];

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    watch,
    setValue,
  } = useForm<FormData>({
    mode: 'onBlur',
    defaultValues: {
      email: user?.email || '',
      title: (user?.title as string) ?? null,
      first_name: user?.first_name || '',
      middle_name: user?.middle_name || '',
      last_name: user?.last_name || '',
      suffix: user?.suffix || '',
      password: '',
      primary_company_id: user?.primary_company_id || '',
      is_admin: user?.is_admin || false,
      status: user?.status || 'active',
      group_ids: user?.groups?.map((g) => g.id) || [],
      enabled_modules: user?.enabled_modules ?? null,
      job_function: user?.job_function ?? null,
      job_function_custom: user?.job_function_custom || '',
      job_level: user?.job_level ?? null,
    },
  });

  const jobFunction = watch('job_function');
  const enabledModules = watch('enabled_modules');
  const selectedGroupIds = watch('group_ids');

  React.useEffect(() => {
    if (open) {
      reset({
        email: user?.email || '',
        title: (user?.title as string) ?? null,
        first_name: user?.first_name || '',
        middle_name: user?.middle_name || '',
        last_name: user?.last_name || '',
        suffix: user?.suffix || '',
        password: '',
        primary_company_id: user?.primary_company_id || '',
        is_admin: user?.is_admin || false,
        status: user?.status || 'active',
        group_ids: user?.groups?.map((g) => g.id) || [],
        enabled_modules: user?.enabled_modules ?? null,
        job_function: user?.job_function ?? null,
        job_function_custom: user?.job_function_custom || '',
        job_level: user?.job_level ?? null,
      });
    }
  }, [open, user, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      if (isEdit && user) {
        const updateData: Record<string, unknown> = {
          title: data.title,
          first_name: data.first_name,
          middle_name: data.middle_name || null,
          last_name: data.last_name,
          suffix: data.suffix || null,
          primary_company_id: data.primary_company_id || null,
          enabled_modules: data.enabled_modules,
          job_function: data.job_function,
          job_function_custom: data.job_function === 'other' ? data.job_function_custom || null : null,
          job_level: data.job_level,
        };
        // Only include admin-only fields if current user is admin
        if (currentUser?.is_admin) {
          updateData.is_admin = data.is_admin;
          updateData.status = data.status;
        }
        await updateMutation.mutateAsync({
          id: user.id,
          data: updateData,
        });
      } else {
        await createMutation.mutateAsync({
          email: data.email,
          title: data.title as UserTitle | null,
          first_name: data.first_name,
          middle_name: data.middle_name || undefined,
          last_name: data.last_name,
          suffix: data.suffix || undefined,
          password: data.password || undefined,
          primary_company_id: data.primary_company_id || undefined,
          is_admin: data.is_admin,
          group_ids: data.group_ids,
          job_function: data.job_function,
          job_function_custom: data.job_function === 'other' ? data.job_function_custom || null : null,
          job_level: data.job_level,
        });
      }
      onClose();
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  const handleGroupToggle = (groupId: string) => {
    const current = selectedGroupIds || [];
    if (current.includes(groupId)) {
      setValue('group_ids', current.filter((id) => id !== groupId));
    } else {
      setValue('group_ids', [...current, groupId]);
    }
  };

  const removeGroup = (groupId: string) => {
    setValue('group_ids', (selectedGroupIds || []).filter((id) => id !== groupId));
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={isEdit ? t('users.edit') : t('users.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={isPending ? t('common:app.loading') : isEdit ? t('common:app.save') : t('common:app.create')}
      isLoading={isPending}
      maxWidth="lg"
    >
      <div className="flex flex-col gap-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>
              {(error as { error?: { message?: string } })?.error?.message || t('common:app.error')}
            </AlertDescription>
          </Alert>
        )}

        <Controller
          name="email"
          control={control}
          rules={{
            required: t('common:validation.required'),
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('common:validation.email') },
          }}
          render={({ field }) => (
            <div className="space-y-2">
              <Label htmlFor="email" required>{t('fields.email')}</Label>
              <Input
                id="email"
                {...field}
                disabled={isEdit}
                className={cn(errors.email && 'border-destructive')}
              />
              {errors.email ? (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              ) : isEdit ? (
                <p className="text-xs text-muted-foreground">{t('users.emailCannotChange')}</p>
              ) : null}
            </div>
          )}
        />

        {/* Name fields: Title + First, Middle + Last, Suffix */}
        <PersonNameFields control={control} errors={errors} />

        {!isEdit && (
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                <Label htmlFor="password">{t('fields.password')}</Label>
                <Input id="password" type="password" {...field} />
                <p className="text-xs text-muted-foreground">{t('users.passwordHelp')}</p>
              </div>
            )}
          />
        )}

        <Controller
          name="primary_company_id"
          control={control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label>{t('fields.company')}</Label>
              <Select
                value={field.value || '_none'}
                onValueChange={(value) => field.onChange(value === '_none' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('fields.none')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">{t('fields.none')}</SelectItem>
                  {companies.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="job_function"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                <Label>{t('fields.jobFunction')}</Label>
                <Select
                  value={field.value || '_none'}
                  onValueChange={(value) => field.onChange(value === '_none' ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('fields.none')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">{t('fields.none')}</SelectItem>
                    {JOB_FUNCTION_OPTIONS.map(({ value, labelKey }) => (
                      <SelectItem key={value} value={value}>{t(labelKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          />

          <Controller
            name="job_level"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                <Label>{t('fields.jobLevel')}</Label>
                <Select
                  value={field.value || '_none'}
                  onValueChange={(value) => field.onChange(value === '_none' ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('fields.none')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">{t('fields.none')}</SelectItem>
                    {JOB_LEVEL_OPTIONS.map(({ value, labelKey }) => (
                      <SelectItem key={value} value={value}>{t(labelKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          />
        </div>

        {jobFunction === 'other' && (
          <Controller
            name="job_function_custom"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                <Label htmlFor="job_function_custom">{t('fields.jobFunctionCustom')}</Label>
                <Input
                  id="job_function_custom"
                  {...field}
                  className={cn(errors.job_function_custom && 'border-destructive')}
                />
                {errors.job_function_custom && (
                  <p className="text-sm text-destructive">{errors.job_function_custom.message}</p>
                )}
              </div>
            )}
          />
        )}

        {currentUser?.is_admin && (
          <Controller
            name="is_admin"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <Switch
                  id="is_admin"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <Label htmlFor="is_admin">{t('users.isAdmin')}</Label>
              </div>
            )}
          />
        )}

        {isEdit && currentUser?.is_admin && (
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                <Label>{t('fields.status')}</Label>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  {/*
                    Deliberately a SUBSET of `user_statuses`, not the whole enum:
                    the four an admin may assign directly. The rest
                    (`pending_verification`, `pending_setup`, `pending_approval`,
                    `rejected`) are lifecycle states owned by registration and
                    tenant approval — offering them here would let an admin fake
                    a stage of a workflow they do not drive.
                  */}
                  <SelectContent>
                    <SelectItem value="active">{t('common:status.active')}</SelectItem>
                    <SelectItem value="inactive">{t('common:status.inactive')}</SelectItem>
                    <SelectItem value="pending">{t('common:status.pending')}</SelectItem>
                    <SelectItem value="locked">{t('common:status.locked')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          />
        )}

        {!isEdit && groups.length > 0 && (
          <div className="space-y-2">
            <Label>{t('users.groups')}</Label>
            {/* Selected groups */}
            {selectedGroupIds && selectedGroupIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {selectedGroupIds.map((id) => {
                  const group = groups.find((g) => g.id === id);
                  if (!group) return null;
                  return (
                    <Badge key={id} variant="secondary" className="gap-1">
                      {group.name}
                      <Button
                        variant="ghost"
                        type="button"
                        onClick={() => removeGroup(id)}
                        className="ms-1 hover:text-destructive h-auto w-auto p-0"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  );
                })}
              </div>
            )}
            {/* Group selector */}
            <Select
              value=""
              onValueChange={(value) => {
                if (value && !selectedGroupIds?.includes(value)) {
                  handleGroupToggle(value);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('users.selectGroup')} />
              </SelectTrigger>
              <SelectContent>
                {groups
                  .filter((g) => !selectedGroupIds?.includes(g.id))
                  .map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Module Access - only show when editing */}
        {isEdit && (
          <>
            <Separator className="my-2" />
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                {t('admin:moduleAccess.title')}
              </h3>
              <p className="text-xs text-muted-foreground">
                {t('admin:moduleAccess.userDescription')}
              </p>
              <ModuleAccessSection
                enabledModules={enabledModules}
                onChange={(modules) => setValue('enabled_modules', modules)}
                availableModules={availableModules}
                showAllToggle={true}
              />
            </div>
          </>
        )}
      </div>
    </DialogForm>
  );
}
